import { createContext, ReactNode, useContext, useEffect } from "react";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useGetNotificationsInfiniteQuery } from "@ac-expo/api";
import { Notification, PaginatedResponse } from "@ac-expo/api-types";
import { useWebSocket } from "./websocket.context";

type Props = {
  children: ReactNode;
};

type NotificationContextType = {
  notifications: InfiniteData<PaginatedResponse<Notification>> | undefined;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isLoading: boolean;
};

const defaultValue = {
  notifications: undefined,
  fetchNextPage: () => undefined,
  hasNextPage: false,
  isLoading: false,
};

const NotificationsContext =
  createContext<NotificationContextType>(defaultValue);

export const useNotificationContext = () => {
  return useContext(NotificationsContext);
};

export const NotificationsProvider = ({ children }: Props) => {
  const { subscribe, socket } = useWebSocket();
  const queryClient = useQueryClient();
  const {
    data: notifications,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useGetNotificationsInfiniteQuery();

  useEffect(() => {
    const notificationsSub = subscribe("shouldRefetchNotifications", () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    });

    return () => {
      notificationsSub();
    };
  }, [queryClient, socket, subscribe]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        fetchNextPage,
        hasNextPage,
        isLoading: isFetching,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
